import { useState } from 'react';
import { useT } from '@hana/i18n';
import { deriveInsights, cite } from '@hana/ask-hana';
import { Segmented, Table, type Column } from '@hana/ui';
import { CCF, DDL, DOC, IB, JOB, LS, MS, OB, RF, SCY, SVCJ, TR, TRF, delta, fi, pc, sum } from './data';
import { BarsPanel, type BarRow } from './panels';
import { SvcTime } from './SvcTime';
import { SvcRole } from './SvcRole';
import { ROLES, people, type Role } from './roleData';
import s from './bod.module.css';
import r from './svcRole.module.css';
import './i18nPerson';

// Mint-BOD · tab "Hiệu suất dịch vụ" (không tiền): nhóm theo Loại job / Khách / Tuyến / Dịch vụ con.
type Grp = 'type' | 'time' | 'cus' | 'role' | 'pod' | 'sub';
type Row = { key: string; name: string; sub?: string; n: number; share?: string; extra?: string };
const CUS_TOP: readonly (readonly [string, number, string])[] = [['RUITONGVN-SC', 221, 'DAVID'], ['GREENBUD', 154, 'DAVID'], ['NAMMY', 130, 'TUANNT'], ['PHUCUONGEXIM', 69, 'DAVID'], ['QUEKYCORP', 68, 'ANDG'], ['TAMNGOC', 64, 'PHILIP'], ['ARRIVE', 64, 'TUANNT'], ['NDP', 53, 'PHILIP'], ['COCOGROUP', 50, 'AnNPH'], ['THANHHAO', 49, 'DAVID'], ['EMDEES', 38, 'ANDG'], ['MKSF', 34, 'PHILIP']];
const POD: readonly (readonly [string, string, number])[] = [['CNNGB', 'Ningbo', 149], ['KRPUS', 'Busan', 125], ['JPTYO', 'Tokyo', 111], ['CNNSA', 'Nansha', 90], ['SAJED', 'Jeddah', 81], ['CNSHK', 'Shekou', 75], ['KRINC', 'Incheon', 72], ['CNXMN', 'Xiamen', 71], ['CNYTN', 'Yantian', 54], ['MYPKG', 'Port Klang', 49], ['JPNGO', 'Nagoya', 38], ['KRKAN', 'Gwangyang', 38]];
const SUB: readonly (readonly [string, readonly number[], string])[] = [['TRF · Vận tải', TRF, '145'], ['DOC · Chứng từ', DOC, '206'], ['CCF · Thông quan', CCF, '175'], ['SCY · Bãi / CY', SCY, '47'], ['DDL · Giao nhận chứng từ', DDL, '79']];
const GROUPS: readonly Grp[] = ['type', 'time', 'cus', 'role', 'pod', 'sub'];

export function SvcTab({ idx, pidx }: { idx: readonly number[]; pidx: readonly number[] }) {
  const t = useT();
  const qp = new URLSearchParams(typeof location === 'undefined' ? '' : location.search).get('grp');
  const [grp, setGrp] = useState<Grp>(GROUPS.includes(qp as Grp) ? (qp as Grp) : 'type');
  const qr = new URLSearchParams(typeof location === 'undefined' ? '' : location.search);
  const [role, setRole] = useState<Role>(ROLES.includes(qr.get('role') as Role) ? (qr.get('role') as Role) : 'SALE');
  const [ppl, setPpl] = useState(qr.get('ppl') ?? '');
  const solo = grp === 'role' && people(role).some((x) => x.nm === ppl);
  const tot = sum(JOB, idx) || 1;
  const jc = sum(JOB, idx);
  const d = delta(jc, sum(JOB, pidx));
  const rows: Row[] = grp === 'type'
    ? ([['OB · xuất khẩu', OB], ['LS · logistics nội địa', LS], ['TR · vận tải', TR], ['IB · nhập khẩu', IB]] as const).map(([name, a]) => ({ key: name, name, n: sum(a, idx), share: pc((sum(a, idx) / tot) * 100) }))
    : grp === 'cus'
      ? CUS_TOP.map(([name, n, sale]) => ({ key: name, name, n, share: pc((n / 1346) * 100), extra: sale }))
      : grp === 'pod'
        ? POD.map(([code, nm, n]) => ({ key: code, name: `→ ${code}`, sub: nm, n, share: pc((n / 1346) * 100) }))
        : SUB.map(([name, a, op]) => ({ key: name, name, n: sum(a, idx), extra: op }));
  const cols: Column<Row>[] = [
    { key: 'name', header: t(`bod.svc.c.${grp}`), width: '34%', cell: (r) => <b>{r.name}</b> },
    ...(grp === 'pod' ? [{ key: 'sub', header: t('bod.svc.c.portName'), width: '20%', cell: (r: Row) => r.sub }] : []),
    { key: 'n', header: t(grp === 'sub' ? 'bod.svc.c.lines' : 'bod.svc.c.jobs'), align: 'right', cell: (r) => fi(r.n) },
    ...(grp !== 'sub' ? [{ key: 'share', header: t('bod.svc.c.share'), align: 'right' as const, cell: (r: Row) => r.share }] : []),
    ...(grp === 'cus' ? [{ key: 'extra', header: 'Sale', cell: (r: Row) => r.extra }] : []),
    ...(grp === 'sub' ? [{ key: 'extra', header: t('bod.svc.c.open'), align: 'right' as const, cell: (r: Row) => r.extra }] : []),
  ];
  const src = cite('job')[0]!;
  const bd = { key: grp, label: t(`bod.svc.k.${grp}`), parts: rows.map((r) => ({ name: r.name, value: r.n })), unit: grp === 'sub' ? t('bod.svc.c.lines') : 'job', source: src };
  const insights = grp === 'type'
    ? deriveInsights({
        at: idx.length === 1 ? idx[0] : undefined,
        series: [{ key: 'jobs', label: t('bod.svc.t.jobs'), periods: MS, values: JOB, unit: 'job', source: src }],
        breakdowns: [bd],
      })
    : grp === 'cus' || grp === 'pod' || grp === 'sub'
      ? deriveInsights({ breakdowns: [bd] })
      : [];
  const rank: BarRow[] | null = grp === 'cus' ? CUS_TOP.map(([k, n]) => ({ k, n, v: fi(n), v2: pc((n / 1346) * 100) }))
    : grp === 'pod' ? POD.map(([c, nm, n]) => ({ k: `→ ${c}`, s: nm, n, v: fi(n), v2: pc((n / 1346) * 100) }))
    : grp === 'sub' ? SUB.map(([k, a, op]) => ({ k, n: sum(a, idx), v: fi(sum(a, idx)), v2: t('bod.svc.openN', { n: op }) })) : null;
  const tiles: readonly (readonly [string, string, string, boolean | null])[] = [
    [t('bod.svc.t.jobs'), fi(jc), d[0], d[1]],
    [t('bod.svc.t.ob'), pc((sum(OB, idx) / tot) * 100), t('bod.svc.t.jobN', { n: fi(sum(OB, idx)) }), null],
    [t('bod.svc.t.rf'), pc((sum(RF, idx) / tot) * 100), t('bod.svc.t.jobN', { n: fi(sum(RF, idx)) }), null],
    [t('bod.svc.t.svcj'), fi(sum(SVCJ, idx)), '', null],
  ];
  return (
    <div className={s.grid}>
      <div className={`${s.span12} ${r.bar}`}>
        <Segmented kind="toggle" label={t('bod.svc.group')} value={grp} onChange={setGrp} items={GROUPS.map((k) => ({ key: k, label: t(`bod.svc.g.${k}`) }))} />
        {grp === 'role' && <Segmented kind="toggle" label={t('bod.role.pick')} value={role} onChange={(k) => { setRole(k); setPpl(''); }} items={ROLES.map((k) => ({ key: k, label: t(`bod.role.r.${k}`) }))} />}
        {grp === 'role' && ppl && <Segmented kind="toggle" label={t('bod.person.pick')} value={solo ? ppl : ''} onChange={setPpl} items={[{ key: '', label: t('bod.person.all') }, { key: ppl, label: ppl }]} />}
      </div>
      {!solo && <section aria-label={t('bod.sec.metrics')} className={`${s.span12} ${s.tiles} ${s.t4}`}>
        {tiles.map(([k, v, sub, good]) => (
          <div key={k} className={s.tile}><span className={s.tk}>{k}</span><b className={s.tv}>{v}</b><span className={`${s.ts} ${good === true ? s.up : good === false ? s.down : ''}`}>{sub}</span></div>
        ))}
      </section>}
      {rank && <BarsPanel title={t(`bod.svc.bar.${grp}`)} sub={t(`bod.svc.bars.${grp}`)} span={8} cols={grp === 'sub' ? 1 : 2} vw={grp === 'sub' ? '72px' : '56px'} rows={rank} />}
      {insights.length > 0 && (
        <section aria-label={t('bod.svc.ins')} className={`${s.card} ${rank ? s.span4 : s.span12}`}>
          <div className={s.ph}><h2>{t('bod.svc.ins')}</h2><span>{t('bod.svc.ins.sub')}</span></div>
          <ul className={s.ins}>{insights.map((x) => <li key={x.kind + x.key}>{x.text}</li>)}</ul>
        </section>
      )}
      {grp === 'time' ? <SvcTime /> : grp === 'role' ? <SvcRole role={role} ppl={ppl} setPpl={setPpl} /> : (
      <section aria-label={t(`bod.svc.k.${grp}`)} className={`${s.card} ${s.span12}`}>
        <div className={s.ph}><h2>{t(`bod.svc.k.${grp}`)}</h2><span>{t(`bod.svc.s.${grp}`)}</span></div>
        <Table label={t(`bod.svc.k.${grp}`)} columns={cols} rows={rows} rowKey={(r) => r.key} dense />
      </section>
      )}
    </div>
  );
}
